import { differenceInCalendarDays } from "date-fns";

import type {
  AutoRedeemSubscriptionRef,
  PendingAutoRedemption,
} from "@bitwarden/web-vault/app/models/types/auto-credit-redemption.types";
import type {
  SubscriptionResponse,
  SubscriptionView,
} from "@bitwarden/web-vault/app/models/view/subscription/subscription.view";

const MONTHLY_CYCLE = "month" as const;

const asNonEmptyString = (value: unknown): string | null => {
  if (value === undefined || value === null) {
    return null;
  }
  const text = String(value).trim();
  return text.length > 0 ? text : null;
};

const safeDateToIso = (date: Date | null | undefined, fallback = ""): string => {
  if (!date || Number.isNaN(date.getTime())) {
    return fallback;
  }
  return date.toISOString();
};

export function toAutoRedeemRef(view: SubscriptionView | null): AutoRedeemSubscriptionRef | null {
  if (!view) {
    return null;
  }

  return {
    active: view.active,
    tier: view.tier,
    payment_cycle: view.paymentCycle,
    payment_provider: view.paymentProvider,
    date_renewal: safeDateToIso(view.renewalDate),
  };
}

const INTERNAL_OUTCOME_STATUSES = new Set(["manual_redeemed", "payment_satisfied"]);

export const isUserNotifiableOutcome = (outcome: { status: string }): boolean =>
  !INTERNAL_OUTCOME_STATUSES.has(outcome.status);

export const toastKeyForAutoRedemptionOutcome = (outcome: PendingAutoRedemption): string => {
  if (outcome.status === "success") {
    return "autoCreditRedemptionSuccess";
  }
  if (outcome.status === "insufficient_credits") {
    return "autoCreditRedemptionInsufficientCredits";
  }
  if (outcome.status === "skipped") {
    switch (outcome.skip_reason) {
      case "apple_not_supported":
        return "autoCreditRedemptionAppleNotSupported";
      case "unsupported_payment_cycle":
        return "autoCreditRedemptionUnsupportedCycle";
      case "inactive_or_non_premium_subscription":
        return "autoCreditRedemptionInactivePlan";
      default:
        return "autoCreditRedemptionSkipped";
    }
  }
  switch (outcome.skip_reason) {
    case "credit_withdrawal_failed":
      return "autoCreditRedemptionWithdrawalFailed";
    case "subscription_redeem_failed":
      return "autoCreditRedemptionFailed";
    case "subscription_discount_failed_credits_refunded":
      return "autoCreditRedemptionCreditsRefunded";
    case "subscription_discount_failed":
      return "autoCreditRedemptionFailedContactSupport";
    default:
      return "autoCreditRedemptionFailed";
  }
};

export const canShowAutoRedeem = (active: AutoRedeemSubscriptionRef | null): boolean => {
  if (!active) {
    return false;
  }

  return (
    active.active === true &&
    active.tier === "premium" &&
    active.payment_cycle === MONTHLY_CYCLE &&
    (active.payment_provider === "stripe" || active.payment_provider === "ironfly")
  );
};

/** Must match gloss-api `AUTO_CREDIT_REDEMPTION_CONFIG.redemptionWindowLeadHours`. */
export const REDEMPTION_WINDOW_LEAD_HOURS = 48;

/**
 * Client-side window check for bootstrap (must match gloss-api eligibility).
 * Eligible from `date_renewal − 48h` onward (UTC instants).
 */
export const isWithinRedemptionWindow = (
  dateRenewal: string | Date | null | undefined,
  now: Date = new Date(),
): boolean => {
  if (!dateRenewal) {
    return false;
  }
  const renewalMs = new Date(
    dateRenewal instanceof Date ? dateRenewal.toISOString() : dateRenewal,
  ).getTime();
  if (Number.isNaN(renewalMs)) {
    return false;
  }
  const windowStartMs = renewalMs - REDEMPTION_WINDOW_LEAD_HOURS * 60 * 60 * 1000;
  return now.getTime() >= windowStartMs;
};

/** Whole days before `date_renewal` at which the insufficient-credits reminder may show (#5490). */
export const RENEWAL_REMINDER_DAYS = [7, 5, 3, 1] as const;

/**
 * Local calendar days from `now` until the renewal date, or null when the date is missing/invalid.
 * Counts calendar dates rather than elapsed 24h periods, so a DST switch can't skew the result.
 */
export const daysUntilRenewal = (
  dateRenewal: Date | null | undefined,
  now: Date = new Date(),
): number | null =>
  !dateRenewal || Number.isNaN(dateRenewal.getTime())
    ? null
    : differenceInCalendarDays(dateRenewal, now);

/**
 * The reminder threshold (7/5/3/1) to fire now, or null when nothing is due.
 * A user who first logs in at 4 days left gets the 5-day reminder once - the earlier 7-day
 * one is treated as shown too, so late logins never produce a burst of popups.
 * @param lowestShown lowest threshold already shown for this renewal, null when none
 */
export const nextRenewalReminderDay = (
  daysLeft: number | null,
  lowestShown: number | null,
): number | null => {
  if (daysLeft === null || daysLeft < 1) {
    return null;
  }
  const candidates = RENEWAL_REMINDER_DAYS.filter((day) => day >= daysLeft);
  if (candidates.length === 0) {
    return null;
  }
  const threshold = Math.min(...candidates);
  return lowestShown !== null && threshold >= lowestShown ? null : threshold;
};

const SUBSCRIPTION_RESPONSE_KEYS: (keyof SubscriptionResponse)[] = [
  "payment_provider",
  "payment_provider_customer_id",
  "payment_cycle",
  "fee",
  "currency",
  "subscription_id",
  "active",
  "date_start",
  "date_end",
  "date_renewal",
  "tier",
  "comment",
  "auto_renew",
  "trial_start",
  "with_card",
];

const parseSubscriptionFee = (value: unknown): number => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  const parsed = parseFloat(String(value ?? ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

/** gloss-api may omit or leave blank optional fields (e.g. trial_start, customer id). */
export const normalizeSubscriptionResponseFromApi = (
  data: Record<string, unknown>,
): SubscriptionResponse | null => {
  const dateStart = asNonEmptyString(data.date_start);
  const dateEnd = asNonEmptyString(data.date_end);
  const dateRenewal = asNonEmptyString(data.date_renewal);

  if (!dateStart || !dateEnd || !dateRenewal) {
    return null;
  }

  if (
    Number.isNaN(new Date(dateStart).getTime()) ||
    Number.isNaN(new Date(dateEnd).getTime()) ||
    Number.isNaN(new Date(dateRenewal).getTime())
  ) {
    return null;
  }

  const paymentProvider = data.payment_provider;
  const paymentCycle = data.payment_cycle;
  const tier = data.tier;

  if (!paymentProvider || !paymentCycle || !tier) {
    return null;
  }

  return {
    payment_provider: paymentProvider as SubscriptionResponse["payment_provider"],
    payment_provider_customer_id: String(data.payment_provider_customer_id ?? ""),
    payment_cycle: paymentCycle as SubscriptionResponse["payment_cycle"],
    fee: parseSubscriptionFee(data.fee),
    currency: (asNonEmptyString(data.currency) ?? "") as SubscriptionResponse["currency"],
    subscription_id: String(data.subscription_id ?? ""),
    active: data.active === true,
    date_start: dateStart,
    date_end: dateEnd,
    date_renewal: dateRenewal,
    tier: tier as SubscriptionResponse["tier"],
    comment: String(data.comment ?? ""),
    auto_renew: data.auto_renew === true,
    trial_start: asNonEmptyString(data.trial_start) ?? dateStart,
    with_card: data.with_card === true,
  };
};

export const subscriptionViewToResponse = (view: SubscriptionView): SubscriptionResponse => {
  const dateStart = safeDateToIso(view.startDate);
  const dateEnd = safeDateToIso(view.endDate);
  const dateRenewal = safeDateToIso(view.renewalDate);

  return {
    payment_provider: view.paymentProvider,
    payment_provider_customer_id: view.paymentProviderCustomerId,
    payment_cycle: view.paymentCycle,
    fee: view.fee,
    currency: view.currency,
    subscription_id: view.id ?? "",
    active: view.active,
    date_start: dateStart,
    date_end: dateEnd,
    date_renewal: dateRenewal || dateEnd || dateStart,
    tier: view.tier,
    comment: view.comment,
    auto_renew: view.autoRenew,
    trial_start: safeDateToIso(view.trialStartDate, dateStart),
    with_card: view.withCard,
  };
};

export const isCompleteSubscriptionResponse = (
  data: Record<string, unknown>,
): data is SubscriptionResponse => normalizeSubscriptionResponseFromApi(data) !== null;

/**
 * Build a full SubscriptionResponse for store sync without overwriting missing getActive fields.
 */
export const resolveSubscriptionResponseFromGetActive = (
  apiPayload: Record<string, unknown>,
  stored: SubscriptionView | null,
): SubscriptionResponse | null => {
  const apiRecord = apiPayload;
  const normalized = normalizeSubscriptionResponseFromApi(apiRecord);

  if (normalized) {
    return normalized;
  }

  if (!stored) {
    return null;
  }

  const merged = subscriptionViewToResponse(stored);
  for (const key of SUBSCRIPTION_RESPONSE_KEYS) {
    const value = apiRecord[key];
    if (value !== undefined && value !== null && value !== "") {
      (merged as Record<string, unknown>)[key] = value;
    }
  }

  return (
    normalizeSubscriptionResponseFromApi(merged as unknown as Record<string, unknown>) ?? merged
  );
};
